/**
 * Admin UI Kit — AntsNest 관리자 공통 디자인 시스템
 * 앱 전반(다이나믹 보드)의 디자인 언어에 맞춘 재사용 styled-components + 소수의 컴포넌트.
 * 보드와 동일한 결: 흰 배경 + 그리드형 테이블(#f2f2f2 헤더, #dee2e6 셀 보더, 줄무늬),
 * 파랑 CTA / 청록 액션 / 회색 보조 버튼, 작은 입력(보더 #ced4da), 청록 페이지네이션.
 *
 * 사용 예:
 *   import { tokens, PageShell, PageHeader, Card, Toolbar, SearchInput,
 *            Select, Button, Table, Th, Td, TableCard, Badge, StatCard,
 *            EmptyState, Pagination } from '../../../components/admin/AdminUI';
 */
import React, { useEffect, useRef, useState } from "react";
import styled, { css, createGlobalStyle } from "styled-components";
import { FiSearch, FiChevronDown, FiInbox, FiChevronLeft, FiChevronRight } from "react-icons/fi";

/* ============================ 디자인 토큰 ============================ */
export const tokens = {
  // 보드와 동일하게 앱 기본 폰트를 상속받는다 (Pretendard 강제 X)
  font: "inherit",
  color: {
    appBg: "#ffffff",
    surface: "#ffffff",
    surfaceMuted: "#f7f8fa",
    tableHead: "#f2f2f2",
    zebra: "#f9f9f9",
    rowHover: "#f5f5f5",
    // 텍스트
    heading: "#212529",
    text: "#343a40",
    muted: "#6c757d",
    subtle: "#868e96",
    // 보더
    border: "#dee2e6",
    borderSubtle: "#e9ecef",
    borderStrong: "#ced4da",
    // 액센트 — 파랑(CTA)
    primary: "#0d6efd",
    primaryHover: "#0b5ed7",
    primaryActive: "#0a58ca",
    primarySoft: "#e7f1ff",
    primaryRing: "rgba(13, 110, 253, 0.25)",
    // 청록 — 일반 액션/페이지네이션
    teal: "#46b5bd",
    tealHover: "#3a99a0",
    tealActive: "#34868c",
    // 회색 — 보조
    gray: "#6c757d",
    grayHover: "#5c636a",
    // 상태
    successText: "#0f5132", successSoft: "#d1e7dd", successDot: "#198754",
    warningText: "#664d03", warningSoft: "#fff3cd", warningDot: "#ffc107",
    dangerText: "#842029", dangerSoft: "#f8d7da", dangerDot: "#dc3545",
    danger: "#dc3545", dangerHover: "#bb2d3b",
  },
  radius: { sm: "4px", md: "6px", lg: "8px", full: "9999px" },
  shadow: {
    xs: "0 1px 2px rgba(0,0,0,0.06)",
    card: "0 2px 10px rgba(0,0,0,0.08)",
    popover: "0 6px 16px rgba(0,0,0,0.15)",
  },
};

const c = tokens.color;

export const AdminGlobalStyle = createGlobalStyle`
  .admin-scope, .admin-scope * { box-sizing: border-box; }
`;

/* ============================ 레이아웃 ============================ */
/* 부모 <main>이 display:flex 이므로 반드시 width:100% 로 채운다 (페이지 폭 불일치 방지) */
export const PageShell = styled.div.attrs({ className: "admin-scope" })`
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 100%;
  background: ${c.appBg};
  font-family: ${tokens.font};
  color: ${c.text};
  text-align: left;
  padding: 20px;
  box-sizing: border-box;
`;

export const PageWidth = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
`;

/* ============================ 페이지 헤더 ============================ */
const HeaderWrap = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 14px;
  margin-bottom: 18px;
  border-bottom: 1px solid ${c.border};

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;
const HeaderText = styled.div`
  min-width: 0;
`;
const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: ${c.heading};
  margin: 0;
`;
const HeaderSubtitle = styled.p`
  font-size: 13px;
  line-height: 1.5;
  color: ${c.muted};
  margin: 4px 0 0;
`;
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  flex-wrap: wrap;
`;

export function PageHeader({ title, subtitle, actions }) {
  return (
    <HeaderWrap>
      <HeaderText>
        <HeaderTitle>{title}</HeaderTitle>
        {subtitle ? <HeaderSubtitle>{subtitle}</HeaderSubtitle> : null}
      </HeaderText>
      {actions ? <HeaderActions>{actions}</HeaderActions> : null}
    </HeaderWrap>
  );
}

/* 보드의 풀폭 파랑 액션바 (예: 신규 등록) */
export const PrimaryBar = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  margin-bottom: 16px;
  border: none;
  border-radius: ${tokens.radius.md};
  background: ${c.primary};
  color: #fff;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color .15s ease;
  &:hover { background: ${c.primaryHover}; }
  & svg { width: 1.05em; height: 1.05em; }
`;

/* ============================ 카드 ============================ */
export const Card = styled.section`
  background: ${c.surface};
  border: 1px solid ${c.border};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.card};
  ${(p) => (p.$pad === false ? "" : `padding: ${p.$pad || "20px"};`)}
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid ${c.border};
`;

export const CardTitle = styled.h2`
  font-size: 15px;
  font-weight: 700;
  color: ${c.heading};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CardBody = styled.div`
  padding: ${(p) => p.$pad || "18px"};
`;

export const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: ${c.heading};
  margin: 0 0 12px;
`;

export const Divider = styled.div`
  height: 1px;
  background: ${c.border};
  margin: ${(p) => p.$my || "16px"} 0;
`;

/* ============================ 툴바 ============================ */
export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 14px;

  @media (max-width: 640px) {
    & > * { flex: 1 1 100%; }
  }
`;

export const Spacer = styled.div`
  flex: 1 1 auto;
`;

/* ============================ 버튼 ============================ */
const sizeStyles = {
  sm: css`height: 32px; padding: 0 12px; font-size: 13px; gap: 5px;`,
  md: css`height: 38px; padding: 0 16px; font-size: 14px; gap: 6px;`,
};
const variantStyles = {
  // 메인 CTA — 파랑
  primary: css`
    background: ${c.primary}; color: #fff; border-color: ${c.primary};
    &:hover { background: ${c.primaryHover}; border-color: ${c.primaryHover}; }
    &:active { background: ${c.primaryActive}; }
  `,
  // 일반 액션 — 청록 (보드의 수정/삭제/엑셀/관리/검색 버튼 톤)
  secondary: css`
    background: ${c.teal}; color: #fff; border-color: ${c.teal};
    &:hover { background: ${c.tealHover}; border-color: ${c.tealHover}; }
    &:active { background: ${c.tealActive}; }
  `,
  // 보조 — 회색 외곽선
  ghost: css`
    background: #fff; color: ${c.gray}; border-color: ${c.borderStrong};
    &:hover { background: ${c.surfaceMuted}; color: ${c.heading}; }
  `,
  // 회색 솔리드 (적용/취소)
  gray: css`
    background: ${c.gray}; color: #fff; border-color: ${c.gray};
    &:hover { background: ${c.grayHover}; border-color: ${c.grayHover}; }
  `,
  danger: css`
    background: ${c.danger}; color: #fff; border-color: ${c.danger};
    &:hover { background: ${c.dangerHover}; border-color: ${c.dangerHover}; }
  `,
  dangerSoft: css`
    background: ${c.dangerSoft}; color: ${c.dangerText}; border-color: transparent;
    &:hover { background: #f5c2c7; }
  `,
};
export const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid transparent;
  border-radius: ${tokens.radius.sm};
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease;
  ${(p) => sizeStyles[p.$size || "md"]}
  ${(p) => variantStyles[p.$variant || "secondary"]}
  &:disabled { opacity: .55; cursor: not-allowed; }
  & svg { width: 1em; height: 1em; }
`;

export const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${(p) => (p.$size === "sm" ? "32px" : "38px")};
  height: ${(p) => (p.$size === "sm" ? "32px" : "38px")};
  border-radius: ${tokens.radius.sm};
  border: 1px solid ${c.borderStrong};
  background: #fff;
  color: ${c.gray};
  cursor: pointer;
  transition: all .15s ease;
  &:hover { background: ${c.surfaceMuted}; color: ${c.heading}; }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

/* ============================ 입력 ============================ */
const fieldBase = css`
  width: 100%;
  height: 38px;
  box-sizing: border-box;
  font-family: inherit;
  font-size: 14px;
  color: ${c.heading};
  background: #fff;
  border: 1px solid ${c.borderStrong};
  border-radius: ${tokens.radius.sm};
  padding: 0 12px;
  transition: border-color .15s ease, box-shadow .15s ease;
  &::placeholder { color: ${c.subtle}; }
  &:focus { outline: none; border-color: ${c.primary}; box-shadow: 0 0 0 3px ${c.primaryRing}; }
  &:disabled { background: ${c.surfaceMuted}; color: ${c.muted}; }
`;
export const Input = styled.input`
  ${fieldBase}
`;
export const Textarea = styled.textarea`
  ${fieldBase}
  height: auto;
  min-height: 96px;
  padding: 10px 12px;
  line-height: 1.5;
  resize: vertical;
`;
export const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: ${c.text};
  margin-bottom: 6px;
`;
export const Field = styled.div`
  margin-bottom: ${(p) => p.$mb || "16px"};
`;

/* 검색 인풋 (아이콘 포함) */
const SearchWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: ${(p) => p.$grow || "0 1 320px"};
  min-width: 0;

  & svg.search-icon {
    position: absolute;
    left: 11px;
    width: 16px; height: 16px;
    color: ${c.subtle};
    pointer-events: none;
  }
  & input { padding-left: 34px; }
`;
export function SearchInput({ value, onChange, onSearch, placeholder = "검색", grow, ...rest }) {
  return (
    <SearchWrap $grow={grow}>
      <FiSearch className="search-icon" />
      <Input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onKeyDown={(e) => {
          if (e.key === "Enter" && onSearch) onSearch(e);
        }}
        {...rest}
      />
    </SearchWrap>
  );
}

/* 커스텀 셀렉트 (chevron 회전) */
const SelectWrap = styled.div`
  position: relative;
  flex: ${(p) => p.$grow || "0 0 auto"};
  min-width: ${(p) => p.$minWidth || "150px"};
`;
const SelectButton = styled.button`
  ${fieldBase}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  text-align: left;
  & .chev { transition: transform .18s ease; color: ${c.subtle}; flex: 0 0 auto; }
  &[data-open="true"] .chev { transform: rotate(180deg); }
  & .ph { color: ${c.subtle}; }
`;
const SelectMenu = styled.ul`
  position: absolute;
  z-index: 30;
  top: calc(100% + 4px);
  left: 0; right: 0;
  margin: 0; padding: 4px;
  list-style: none;
  background: #fff;
  border: 1px solid ${c.border};
  border-radius: ${tokens.radius.md};
  box-shadow: ${tokens.shadow.popover};
  max-height: 260px;
  overflow-y: auto;
`;
const SelectOption = styled.li`
  padding: 9px 10px;
  font-size: 14px;
  color: ${c.text};
  border-radius: ${tokens.radius.sm};
  cursor: pointer;
  transition: background-color .12s ease;
  &:hover { background: ${c.surfaceMuted}; }
  &[data-active="true"] { background: ${c.primarySoft}; color: ${c.primary}; font-weight: 700; }
`;
export function Select({ value, onChange, options = [], placeholder = "선택", grow, minWidth }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);
  const selected = options.find((o) => o.value === value);
  return (
    <SelectWrap ref={ref} $grow={grow} $minWidth={minWidth}>
      <SelectButton type="button" data-open={open} onClick={() => setOpen((o) => !o)}>
        <span className={selected ? "" : "ph"}>{selected ? selected.label : placeholder}</span>
        <FiChevronDown className="chev" size={16} />
      </SelectButton>
      {open && (
        <SelectMenu>
          {options.map((o) => (
            <SelectOption
              key={o.value}
              data-active={o.value === value}
              onClick={() => { onChange && onChange(o.value); setOpen(false); }}
            >
              {o.label}
            </SelectOption>
          ))}
        </SelectMenu>
      )}
    </SelectWrap>
  );
}

export const Checkbox = styled.input.attrs({ type: "checkbox" })`
  width: 16px; height: 16px;
  accent-color: ${c.primary};
  cursor: pointer;
  margin: 0;
`;

/* ============================ 테이블 (그리드형, 보드와 동일) ============================ */
export const TableCard = styled.div`
  background: #fff;
  border: 1px solid ${c.border};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.card};
  overflow: hidden;
`;
export const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;
export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: ${c.text};

  tbody tr:nth-child(even) td { background: ${c.zebra}; }
  tbody tr:hover td { background: ${c.rowHover}; }
`;
export const Th = styled.th`
  text-align: ${(p) => p.$align || "left"};
  font-size: 13px;
  font-weight: 700;
  color: ${c.heading};
  background: ${c.tableHead};
  padding: 9px 12px;
  border: 1px solid ${c.border};
  white-space: nowrap;
  position: ${(p) => (p.$sticky ? "sticky" : "static")};
  top: 0;
`;
export const Td = styled.td`
  text-align: ${(p) => p.$align || "left"};
  padding: 9px 12px;
  color: ${(p) => (p.$muted ? c.muted : c.text)};
  border: 1px solid ${c.border};
  vertical-align: middle;
`;
export const Tr = styled.tr`
  ${(p) => p.$selected && css`& > td { background: ${c.primarySoft} !important; }`}
`;

/* ============================ 배지 / 태그 ============================ */
const badgeTones = {
  neutral: css`background: ${c.surfaceMuted}; color: ${c.muted}; box-shadow: inset 0 0 0 1px ${c.border};`,
  brand:   css`background: ${c.primarySoft}; color: ${c.primary};`,
  success: css`background: ${c.successSoft}; color: ${c.successText};`,
  warning: css`background: ${c.warningSoft}; color: ${c.warningText};`,
  danger:  css`background: ${c.dangerSoft}; color: ${c.dangerText};`,
};
export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px;
  border-radius: ${tokens.radius.full};
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
  ${(p) => badgeTones[p.$tone || "neutral"]}

  &::before {
    ${(p) => p.$dot && css`
      content: "";
      width: 6px; height: 6px; border-radius: 50%;
      background: ${
        p.$tone === "success" ? c.successDot :
        p.$tone === "warning" ? c.warningDot :
        p.$tone === "danger" ? c.dangerDot :
        p.$tone === "brand" ? c.primary : c.subtle
      };
    `}
  }
`;
export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: ${tokens.radius.sm};
  font-size: 12px;
  font-weight: 600;
  background: ${c.surfaceMuted};
  color: ${c.muted};
  box-shadow: inset 0 0 0 1px ${c.border};
  white-space: nowrap;
`;

/* ============================ 통계 카드 ============================ */
export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols || 4}, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;
const StatCardBox = styled.div`
  background: #fff;
  border: 1px solid ${c.border};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.xs};
  padding: 16px 18px;
`;
const StatTop = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 8px;
`;
const StatLabel = styled.span`
  font-size: 13px; color: ${c.muted}; font-weight: 600;
`;
const StatIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border-radius: ${tokens.radius.md};
  background: ${(p) => p.$soft || c.primarySoft};
  color: ${(p) => p.$color || c.primary};
  & svg { width: 17px; height: 17px; }
`;
const StatValue = styled.div`
  font-size: 24px; font-weight: 700; color: ${c.heading};
  line-height: 1.1;
`;
const StatHint = styled.div`
  font-size: 12px; color: ${c.subtle}; margin-top: 6px;
`;
export function StatCard({ label, value, hint, icon, color, soft }) {
  return (
    <StatCardBox>
      <StatTop>
        <StatLabel>{label}</StatLabel>
        {icon ? <StatIcon $color={color} $soft={soft}>{icon}</StatIcon> : null}
      </StatTop>
      <StatValue style={color ? { color } : undefined}>{value}</StatValue>
      {hint ? <StatHint>{hint}</StatHint> : null}
    </StatCardBox>
  );
}

/* ============================ 빈 상태 ============================ */
const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${(p) => p.$pad || "48px 24px"};
  color: ${c.muted};
`;
const EmptyIcon = styled.div`
  display: inline-flex; align-items: center; justify-content: center;
  width: 46px; height: 46px; border-radius: ${tokens.radius.lg};
  background: ${c.surfaceMuted};
  color: ${c.subtle};
  margin-bottom: 12px;
  & svg { width: 22px; height: 22px; }
`;
const EmptyTitle = styled.div`
  font-size: 15px; font-weight: 700; color: ${c.text}; margin-bottom: 4px;
`;
const EmptyDesc = styled.div`
  font-size: 13px; color: ${c.muted}; max-width: 360px; line-height: 1.5;
`;
export function EmptyState({ icon, title = "데이터가 없습니다", description, action, pad }) {
  return (
    <EmptyBox $pad={pad}>
      <EmptyIcon>{icon || <FiInbox />}</EmptyIcon>
      <EmptyTitle>{title}</EmptyTitle>
      {description ? <EmptyDesc>{description}</EmptyDesc> : null}
      {action ? <div style={{ marginTop: 16 }}>{action}</div> : null}
    </EmptyBox>
  );
}

/* ============================ 페이지네이션 (청록 활성) ============================ */
const PagerWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${(p) => p.$justify || "space-between"};
  gap: 10px;
  padding: 14px 16px;
  border-top: 1px solid ${c.border};
  flex-wrap: wrap;
`;
const PagerInfo = styled.div`
  font-size: 13px; color: ${c.muted};
`;
const PagerBtns = styled.div`
  display: flex; align-items: center; gap: 4px;
`;
const PageNum = styled.button`
  min-width: 34px; height: 34px; padding: 0 8px;
  border-radius: ${tokens.radius.sm};
  font-family: inherit; font-size: 13px; font-weight: 600;
  cursor: pointer;
  transition: all .12s ease;
  ${(p) => p.$active
    ? css`background: ${c.teal}; color: #fff; border: 1px solid ${c.teal};`
    : css`background: #fff; color: ${c.text}; border: 1px solid ${c.border};
          &:hover { background: ${c.surfaceMuted}; border-color: ${c.borderStrong}; }`}
  &:disabled { color: ${c.subtle}; cursor: not-allowed; background: #fff; border-color: ${c.border}; }
  display: inline-flex; align-items: center; justify-content: center;
`;
function pageRange(current, total) {
  const delta = 1;
  const range = [];
  const left = Math.max(2, current - delta);
  const right = Math.min(total - 1, current + delta);
  range.push(1);
  if (left > 2) range.push("…");
  for (let i = left; i <= right; i++) range.push(i);
  if (right < total - 1) range.push("…");
  if (total > 1) range.push(total);
  return range;
}
export function Pagination({ page, totalPages, onChange, totalItems, pageSize }) {
  if (totalPages <= 0) return null;
  const info = totalItems != null && pageSize != null
    ? `총 ${totalItems}건 · ${Math.min((page - 1) * pageSize + 1, totalItems)}–${Math.min(page * pageSize, totalItems)}`
    : `${page} / ${totalPages} 페이지`;
  return (
    <PagerWrap>
      <PagerInfo>{info}</PagerInfo>
      <PagerBtns>
        <PageNum disabled={page <= 1} onClick={() => onChange(page - 1)} aria-label="이전">
          <FiChevronLeft size={16} />
        </PageNum>
        {pageRange(page, totalPages).map((p, i) =>
          p === "…" ? (
            <PageNum key={`e${i}`} as="span" disabled style={{ border: "none", background: "transparent" }}>…</PageNum>
          ) : (
            <PageNum key={p} $active={p === page} onClick={() => onChange(p)}>{p}</PageNum>
          )
        )}
        <PageNum disabled={page >= totalPages} onClick={() => onChange(page + 1)} aria-label="다음">
          <FiChevronRight size={16} />
        </PageNum>
      </PagerBtns>
    </PagerWrap>
  );
}

/* 좌측 리스트(사이드 패널) — 마스터/디테일 패턴용 */
export const SideList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
export const SideItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: 14px;
  color: ${(p) => (p.$active ? c.primary : c.text)};
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  background: ${(p) => (p.$active ? c.primarySoft : "transparent")};
  border: 1px solid ${(p) => (p.$active ? c.primarySoft : "transparent")};
  border-radius: ${tokens.radius.sm};
  padding: 9px 11px;
  cursor: pointer;
  transition: background-color .12s ease, color .12s ease;
  &:hover { background: ${(p) => (p.$active ? c.primarySoft : c.surfaceMuted)}; }
`;
